/**
 * A pergunta localizada do Conferir envio (E144.F4, ADR-0098 §2.2): o arquivo de apoio que não deu
 * para atribuir a uma CCB pergunta a qual ela pertence — uma das CCBs, todas, ou (quando o arquivo
 * pode ser contrato) outra CCB. A resposta vai à conclusão e decide o caso em que ele entra. A CCB
 * declarada assim já é destino dos outros apoios (F-E144.F4-02), menos do próprio arquivo.
 */
import { Blueprint, CardKicker } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import {
  RESPOSTA_OUTRO_CONTRATO,
  RESPOSTA_TODAS,
  type ContratoDoEnvioData,
  type PerguntaDoApoioData,
} from '@/lib/abertura_do_caso';

import { chaveDoDeclarado } from './contratosDeclarados';
import { rotuloDoContrato } from './rotulosDoEnvio';

const t = ptBR.abertura.conferirEnvio;

export interface PerguntasDoApoioProps {
  perguntas: readonly PerguntaDoApoioData[];
  contratos: readonly ContratoDoEnvioData[];
  respostas: Readonly<Record<string, string>>;
  onResponder: (documentoId: string, resposta: string) => void;
  /** A tentativa de concluir com pergunta sem resposta: as que faltam ficam marcadas. */
  apontarPendentes: boolean;
  disabled: boolean;
}

export function PerguntasDoApoio(props: PerguntasDoApoioProps): JSX.Element | null {
  const { perguntas, contratos, respostas, onResponder, disabled } = props;
  if (perguntas.length === 0) return null;
  return (
    <Blueprint
      as="section"
      className="flex flex-col gap-sc-4 p-sc-6"
      data-testid="conferir-perguntas"
    >
      <CardKicker className="block">{t.perguntaTitulo}</CardKicker>
      <p className="text-body-sm text-fg-muted">{t.perguntaTexto}</p>
      {perguntas.map(({ unidade, pode_ser_contrato }) => {
        const opcoes: [string, string][] = [
          ...contratos
            .filter((c) => c.chave !== chaveDoDeclarado(unidade.documento_id))
            .map((c): [string, string] => [c.chave, rotuloDoContrato(c)]),
          [RESPOSTA_TODAS, t.todas],
          ...(pode_ser_contrato
            ? [[RESPOSTA_OUTRO_CONTRATO, t.outroContrato] as [string, string]]
            : []),
        ];
        const pendente = props.apontarPendentes && respostas[unidade.documento_id] === undefined;
        return (
          <fieldset
            key={unidade.documento_id}
            className="flex flex-col gap-sc-2"
            data-testid={`conferir-pergunta-${unidade.documento_id}`}
            aria-invalid={pendente}
          >
            <legend className="text-body-sm font-semibold text-fg">{unidade.nome}</legend>
            {opcoes.map(([valor, rotulo]) => (
              <label key={valor} className="flex items-center gap-sc-2 text-body-sm text-fg">
                <input
                  type="radio"
                  name={`apoio-${unidade.documento_id}`}
                  value={valor}
                  checked={respostas[unidade.documento_id] === valor}
                  disabled={disabled}
                  onChange={() => onResponder(unidade.documento_id, valor)}
                />
                {rotulo}
              </label>
            ))}
            {pendente ? (
              <p role="alert" className="text-caption text-danger">
                {t.perguntaPendente}
              </p>
            ) : null}
          </fieldset>
        );
      })}
    </Blueprint>
  );
}
